<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useQuasar } from 'quasar';

import ReorderableTable from '@/components/ReorderableTable.vue';

import { useNewsItemsStore } from '@/stores/newsItems.store';
import type { ProjectModuleNewsItemConfigDto, ProjectModuleDto, Locales } from '@/services/types/api.types';
import { getTranslation } from '@/composables/data-display-utils';
import router from '@/router';
import { formatDate } from '@/plugins/i18n';
import NewsItemDialog from '@/components/dialogs/NewsItemDialog.vue';

// CONST
const { t } = useI18n();
const $q = useQuasar();

// STORE
const newsItemsStore = useNewsItemsStore();
const { orderedNewsListItems } = storeToRefs(newsItemsStore);

// PROPS
const props = defineProps<{
  module: ProjectModuleDto;
  languageEnabled: Locales[];
}>();

// COMPUTED
const columns = computed(() => [
  {
    name: 'title',
    label: t('global.label.title'),
    field: (row: ProjectModuleNewsItemConfigDto) => getTranslation(row, 'title', props.languageEnabled),
    align: 'left',
  },
  {
    name: 'status',
    label: t('global.label.status'),
    field: (row: ProjectModuleNewsItemConfigDto) => getStatus(row),
    align: 'left',
  },
  {
    name: 'date',
    label: t('global.label.date'),
    field: (row: ProjectModuleNewsItemConfigDto) => {
      return row.date ? formatDate(new Date(row.date)) : '';
    },
    align: 'left',
  },
]);

// LIFECYCLE
onMounted(async () => {
  if (props.module.id) {
    await newsItemsStore.fetchNewsListItemsByModuleId(props.module.id);
  }
});

// FUNCTIONS
function getStatus(newsItem: ProjectModuleNewsItemConfigDto) {
  const status = newsItemsStore.getStatuses();
  return status.find((s) => s.value === newsItem.status)?.label ?? String(newsItem.status);
}

const handleAddRow = () => {
  if (props.module.id) {
    const newsItemConfig: ProjectModuleNewsItemConfigDto = newsItemsStore.createNewsListItem(props.module.id);
    $q.dialog({
      component: NewsItemDialog,
      componentProps: {
        mode: 'add',
        newsItemConfig,
        languageEnabled: props.languageEnabled,
        module: props.module,
      },
    });
  }
};

const handleDeleteRow = (row: ProjectModuleNewsItemConfigDto) => {
  $q.dialog({
    title: t('global.dialogs.deleteTitle', { type: t('project-module.news.title') }),
    message: t('global.dialogs.deleteMessage', { type: t('project-module.news.title') }),
    cancel: true,
    persistent: true,
  }).onOk(async () => {
    await newsItemsStore.deleteNewsListItem(row);
  });
};

const handleRowClick = (row: ProjectModuleNewsItemConfigDto) => {
  router.push({
    name: 'news_item_details',
    params: { id: row.id, projectId: props.module.project_id, moduleId: props.module.id },
  });
};
</script>
<template>
  <div class="news-list">
    <reorderable-table
      :rows="orderedNewsListItems"
      :columns="columns"
      :title="t('project-module.news.title')"
      itemKey="id"
      @row-click="handleRowClick"
      @add-row="handleAddRow"
      @delete-row="handleDeleteRow"
      :disableOrderable="true"
    />
  </div>
</template>
